import {useMemo, useState} from 'react';

import {ProcessSingletonTaskSchemaName} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import buildStaticDataFromFlowchartConfigGraphQL from './buildStaticDataFromFlowchartConfigGraphQL';
import {
  ProcessFlowchartConfigVersionGQL,
  ProcessFlowchartConfigWithValidSchemaGQL,
  StaticTemplateFlowchart,
  FlowchartStaticEdgeData,
} from './processFlowchartConfigEditorConstants';
import {Transition} from './types/transition/TransitionTypes';
import {ProcessTaskDefinitionsResult} from './useAllSchemasById';

interface UseFlowchartConfigDataArgs {
  flowchartConfigVersion: ProcessFlowchartConfigVersionGQL | null;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartConfig: ProcessFlowchartConfigWithValidSchemaGQL;
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  singletonTaskSchemasByName: Record<
    ProcessSingletonTaskSchemaName,
    ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  >;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  processTaskDefinitionsResult: ProcessTaskDefinitionsResult;
}

interface UseFlowchartConfigDataResult {
  nodesStaticData: StaticTemplateFlowchart['nodesStaticData'];
  edgesStaticData: Array<FlowchartStaticEdgeData>;
  initialTransitions: Record<string, Transition>;
  didInitialConfigHaveConditionalMapping: boolean;
  didInitialFlowchartHaveChainedRouters: boolean;
  initialCidToIdMap: CidIdMap;
  schemaMapperPtdvIdToTaskSchemaId: Record<string, string>;
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  taskDefinitionsResult: ProcessTaskDefinitionsResult;
}

/**
 * Converts GraphQL flowchart shape to a static representation of nodes and edges,
 * and filters task definitions by allowed execution types.
 *
 * Extracted from ProcessFlowchartConfigDataTransformer.
 */
export default function useFlowchartConfigData(
  args: UseFlowchartConfigDataArgs
): UseFlowchartConfigDataResult {
  const {
    flowchartConfigVersion,
    rootTaskSchema,
    flowchartConfig,
    availableTaskSchemas: availableTaskSchemasFromVersion,
    singletonTaskSchemasByName,
    flowchartEditorConfigurator,
    processTaskDefinitionsResult,
  } = args;

  const {
    nodesStaticData,
    edgesStaticData,
    transitions: initialTransitions,
    didInitialConfigHaveConditionalMapping: initialDidConfigHaveConditionalMapping,
    availableTaskSchemas,
    hasChainedRouters: initialFlowchartHasChainedRouters,
    cidToIdMap: initialCidToIdMap,
    schemaMapperPtdvIdToTaskSchemaId = {},
  } = useMemo(
    () =>
      buildStaticDataFromFlowchartConfigGraphQL({
        flowchartConfigVersion,
        rootTaskSchema,
        rootTaskDefinition: flowchartConfig.taskDefinition,
        allTaskSchemas: [
          rootTaskSchema,
          ...availableTaskSchemasFromVersion,
          ...Object.values(singletonTaskSchemasByName),
        ],
        flowchartEditorConfigurator,
        editorMode: FlowchartEditorMode.MAIN_CANVAS,
      }),
    [
      flowchartConfigVersion,
      rootTaskSchema,
      flowchartConfig.taskDefinition,
      availableTaskSchemasFromVersion,
      singletonTaskSchemasByName,
      flowchartEditorConfigurator,
    ]
  );

  const [didInitialConfigHaveConditionalMapping, _setDidInitialConfigHaveConditionalMapping] = useState(
    initialDidConfigHaveConditionalMapping
  );
  const [didInitialFlowchartHaveChainedRouters, _setDidInitialFlowchartHaveChainedRouters] = useState(
    initialFlowchartHasChainedRouters
  );

  const taskDefinitionsResult: ProcessTaskDefinitionsResult = useMemo(() => {
    if (processTaskDefinitionsResult == null) {
      return null;
    }
    const nodeExecutionTypes = flowchartEditorConfigurator.allowedNodeExecutionTypes;
    return processTaskDefinitionsResult.filter((definition) =>
      nodeExecutionTypes.includes(definition.latestVersion.processTaskSchema.executionType)
    );
  }, [flowchartEditorConfigurator, processTaskDefinitionsResult]);

  return {
    nodesStaticData,
    edgesStaticData,
    initialTransitions,
    didInitialConfigHaveConditionalMapping,
    didInitialFlowchartHaveChainedRouters,
    initialCidToIdMap,
    schemaMapperPtdvIdToTaskSchemaId,
    availableTaskSchemas,
    taskDefinitionsResult,
  };
}
